import { useEffect, useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import LayeredStack from "@/components/LayeredStack";
import { Bot, Flame, Trophy, User, Swords, Shield, Gauge, Heart } from "lucide-react";

const STAT_ICON = { attack: Swords, defense: Shield, speed: Gauge, health: Heart };

export default function RobotLookup() {
  const { id } = useParams();
  const { user } = useOutletContext();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/robots/lookup/${id}`).then((r) => setD(r.data)).catch(() => setErr(true)); }, [id]);

  if (err) return <div className="panel p-8 text-center text-muted-foreground" data-testid="lookup-notfound">Robot not found in the registry.</div>;
  if (!d) return null;
  const p = d.pet;

  return (
    <div data-testid="robot-lookup-page" className="max-w-2xl mx-auto">
      <span className="label-cap text-primary flex items-center gap-2"><Bot className="w-4 h-4" /> Robot Registry // Public Lookup</span>
      <div className="panel-glow p-6 mt-3 hud-corner">
        <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
          <div className="rounded-xl overflow-hidden border border-primary/40 bg-background/50 shrink-0">
            <LayeredStack layers={p.layers} core={p.core_color} rig={p.rig} cpu={p.cpu} size={180} state="idle" />
          </div>
          <div className="flex-1 min-w-0 text-center sm:text-left">
            <h1 data-testid="lookup-name" className="font-head font-black text-3xl">{p.name}</h1>
            <p className="label-cap flex items-center justify-center sm:justify-start gap-1 mt-1"><User className="w-3 h-3" /> Pilot: {d.owner.name}{d.mine && " (you)"}</p>
            <div className="flex items-center justify-center sm:justify-start gap-3 mt-3 text-sm">
              <span className="flex items-center gap-1 text-accent mono"><Flame className="w-4 h-4" /> Power {d.power}</span>
              <span className="flex items-center gap-1 text-secondary mono"><Trophy className="w-4 h-4" /> {d.owner.arena_wins} wins</span>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-4">
              {["attack", "defense", "speed", "health"].map((s) => {
                const Icon = STAT_ICON[s];
                return (
                  <div key={s} className="panel p-2 text-center" data-testid={`lookup-stat-${s}`}>
                    <Icon className="w-4 h-4 mx-auto text-primary mb-0.5" />
                    <div className="font-head font-bold mono text-sm">{p.stats[s]}</div>
                    <div className="label-cap text-[8px]">{s}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <div className="label-cap mb-2 flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5 text-secondary" /> Accolades</div>
          {d.owner.trophies.length === 0 ? (
            <p className="text-sm text-muted-foreground">No spotlight trophies yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="lookup-trophies">
              {d.owner.trophies.map((t, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary/15 border border-secondary/40 text-xs font-bold text-secondary">
                  <Trophy className="w-3.5 h-3.5" /> {t.category} · {t.week}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
